import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, doc, setDoc,
  deleteDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from '../../config/firebase';
import { createUserWithEmailAndPassword,
  sendPasswordResetEmail } from 'firebase/auth';
import Sidebar from '../Shared/Sidebar';
import { v4 as uuidv4 } from 'uuid';

export default function Staff() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    region: '',
    role: 'staff',
    accessLevel: 'basic',
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  async function fetchStaff() {
    try {
      const snap = await getDocs(collection(db, 'users'));
      const staffOnly = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role === 'staff' || u.role === 'admin');
      setStaffList(staffOnly);
    } catch (err) {
      console.error(err);
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleCreateStaff(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Generate temporary password
      const tempPassword = `Iy${uuidv4().slice(0, 8)}@2026`;

      // Create Firebase Auth account
      const result = await createUserWithEmailAndPassword(
        auth,
        form.email,
        tempPassword
      );

      // Save to Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        region: form.region,
        role: form.role,
        accessLevel: form.accessLevel,
        createdAt: serverTimestamp(),
      });

      // Send password reset so they set their own
      await sendPasswordResetEmail(auth, form.email);

      setSuccess(
        `Account created for ${form.firstName}! ` +
        `A password setup email has been sent to ${form.email}`
      );

      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        region: '',
        role: 'staff',
        accessLevel: 'basic',
      });

      setShowForm(false);
      fetchStaff();

    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered.');
      } else {
        setError('Something went wrong. Please try again.');
      }
      console.error(err);
    }
    setLoading(false);
  }

  async function handleDelete(staffId, email) {
    if (window.confirm(
      `Are you sure you want to remove ${email}?`
    )) {
      try {
        await deleteDoc(doc(db, 'users', staffId));
        fetchStaff();
        setSuccess('Staff account removed successfully!');
      } catch (err) {
        setError('Could not remove staff. Try again.');
      }
    }
  }

  async function handleResetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(`Password reset email sent to ${email}`);
    } catch (err) {
      setError('Could not send reset email. Try again.');
    }
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Staff Management
            </div>
            <div style={styles.pageSub}>
              Create and manage staff and admin accounts
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ Add Staff'}
          </button>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        {/* CREATE STAFF FORM */}
        {showForm && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Create New Account
            </div>
            <form onSubmit={handleCreateStaff}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    First Name *
                  </label>
                  <input
                    style={styles.formInput}
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="e.g. Sarah"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Last Name *
                  </label>
                  <input
                    style={styles.formInput}
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="e.g. Mwamba"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Email Address *
                  </label>
                  <input
                    style={styles.formInput}
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="sarah@email.com"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Phone Number
                  </label>
                  <input
                    style={styles.formInput}
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+255 7XX XXX XXX"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Region
                  </label>
                  <input
                    style={styles.formInput}
                    name="region"
                    value={form.region}
                    onChange={handleChange}
                    placeholder="e.g. Dar es Salaam"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Role *
                  </label>
                  <select
                    style={styles.formInput}
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="staff">
                      Field Staff
                    </option>
                    <option value="admin">
                      Admin
                    </option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Access Level
                  </label>
                  <select
                    style={styles.formInput}
                    name="accessLevel"
                    value={form.accessLevel}
                    onChange={handleChange}
                  >
                    <option value="basic">
                      Basic — View & Record Only
                    </option>
                    <option value="standard">
                      Standard — Forms & Reports
                    </option>
                  </select>
                </div>
              </div>
              <div style={styles.formNote}>
                📧 A password setup email will be sent
                automatically to the staff member's email.
                They will set their own password.
              </div>
              <button
                type="submit"
                style={styles.submitBtn}
                disabled={loading}
              >
                {loading
                  ? 'Creating...'
                  : 'Create Account & Send Email'}
              </button>
            </form>
          </div>
        )}

        {/* STAFF LIST */}
        <div style={styles.panel}>
          <div style={styles.sectionTitle}>
            All Staff & Admins ({staffList.length})
          </div>
          {staffList.length === 0 ? (
            <div style={styles.empty}>
              No staff yet. Add your first staff member!
            </div>
          ) : (
            staffList.map((s) => (
              <div key={s.id} style={styles.staffRow}>
                <div style={{
                  ...styles.staffAvatar,
                  background: s.role === 'admin'
                    ? 'linear-gradient(135deg, #4527A0, #7E57C2)'
                    : 'linear-gradient(135deg, #14452F, #1F6B45)'
                }}>
                  {s.firstName?.[0]}{s.lastName?.[0]}
                </div>
                <div style={styles.staffInfo}>
                  <div style={styles.staffName}>
                    {s.firstName} {s.lastName}
                  </div>
                  <div style={styles.staffMeta}>
                    📧 {s.email} ·
                    {s.region || 'No region'}
                  </div>
                </div>
                <span style={{
                  ...styles.roleBadge,
                  background: s.role === 'admin'
                    ? '#EDE7F6'
                    : '#EAF3EC',
                  color: s.role === 'admin'
                    ? '#4527A0'
                    : '#14452F',
                }}>
                  {s.role === 'admin'
                    ? 'Admin'
                    : 'Staff'}
                </span>
                <div style={styles.actions}>
                  <button
                    style={styles.btnReset}
                    onClick={() =>
                      handleResetPassword(s.email)
                    }
                  >
                    Reset Password
                  </button>
                  <button
                    style={styles.btnDelete}
                    onClick={() =>
                      handleDelete(s.id, s.email)
                    }
                  >
                    Futa
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(245,124,0,0.25)',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    color: '#C62828',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
    marginBottom: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  formLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  formInput: {
    padding: '11px 14px',
    borderRadius: '9px',
    border: '1.5px solid #E5E7EB',
    background: '#FAF9F5',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  formNote: {
    background: '#FFF3E0',
    border: '1px solid #FFE0B2',
    color: '#E65100',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '12.5px',
    marginBottom: '14px',
    lineHeight: 1.5,
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(20,69,47,0.25)',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
  staffRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 0',
    borderBottom: '1px solid #F3F4F6',
    flexWrap: 'wrap',
  },
  staffAvatar: {
    width: '42px',
    height: '42px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    flexShrink: 0,
  },
  staffInfo: {
    flex: 1,
    minWidth: '180px',
  },
  staffName: {
    fontSize: '14px',
    fontWeight: '600',
  },
  staffMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
    marginTop: '2px',
  },
  roleBadge: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontWeight: '600',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  btnReset: {
    background: '#FFF3E0',
    color: '#E65100',
    border: 'none',
    padding: '7px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnDelete: {
    background: '#FFEBEE',
    color: '#C62828',
    border: 'none',
    padding: '7px 10px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};